import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import { Container, FormControl, Grid, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material';

import PageTitleWrapper from 'components/PageTitleWrapper';
import TimeWindows from 'components/TimeWindows';
import RefreshTimer from 'components/RefreshTimer';
import {
  getEcosystemDetailIndex,
  getEcosystemUsage,
  getEcosystemThreatData,
  getEcosystemNetworkMetric,
  getEcosystemThreatMetric,
  getEcosystemDetailCounters,
  getEcosystemThreatSeverity,
  setTimeWindow,
  setDetailLoading,
  setEcosystem,
} from 'store/ecosystems/actions';

import LoadingSpinner from 'components/LoadingSpinner';
import ThreatGeoLocation from './ThreatGeoLocation';
import Utilization from './Utilization';
import Security from './Security';
import ThreatMetrics from './ThreatMetrics';

const getTimeStamp = (timeWindow) => {
  const date = new Date();
  return {
    to: date.toISOString(),
    from: new Date(date.setHours(date.getHours() - timeWindow)).toISOString(),
  };
};

const timeWindowOptions = [
  { label: '8 hours', value: 8 },
  { label: '24 hours', value: 24 },
];

const ThreatReport = () => {
  const dispatch = useDispatch();
  const { timeWindow, refresh, items: ecosystems } = useSelector((state) => state.ecosystems);

  const [loading, setLoading] = useState(false);
  const [timeStamp, setTimeStamp] = useState(null);
  const [intervalId, setIntervalId] = useState(null);
  const [selectedEcosystemUUID, setSelectedEcosystemUUID] = useState(ecosystems?.[0].uuid);

  const ecosystemOptions = useMemo(
    () => ecosystems.map(({ name, uuid }) => ({ label: name, value: uuid })),
    [ecosystems],
  );

  const selectedEcosystem = useMemo(
    () => ecosystems.find(({ uuid }) => uuid === selectedEcosystemUUID),
    [ecosystems, selectedEcosystemUUID],
  );

  useEffect(() => {
    if (selectedEcosystem) dispatch(setEcosystem(selectedEcosystem));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEcosystem]);

  const handleChangeTimeWindow = useCallback(
    (value) => {
      dispatch(setTimeWindow(value));
    },
    [dispatch],
  );

  const handleRefresh = useCallback(() => {
    setTimeStamp(getTimeStamp(timeWindow));
  }, [timeWindow]);

  useEffect(() => {
    handleRefresh();
  }, [handleRefresh, timeWindow]);

  useEffect(() => {
    if (intervalId) clearInterval(intervalId);
    if (refresh) {
      const interval = setInterval(() => {
        handleRefresh();
      }, refresh * 60 * 1000);
      setIntervalId(interval);
    }
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refresh]);

  const getData = useCallback(async () => {
    if (!selectedEcosystemUUID || !timeStamp) return;
    const payload = {
      timeStamp,
      ecosystemUUID: selectedEcosystemUUID,
    };
    try {
      dispatch(setDetailLoading());
      dispatch(getEcosystemDetailIndex(payload));
      dispatch(getEcosystemUsage(payload));
      dispatch(getEcosystemNetworkMetric({ ...payload, attributes: 'application' }));
      dispatch(getEcosystemNetworkMetric({ ...payload, attributes: 'user' }));
      dispatch(getEcosystemNetworkMetric({ ...payload, attributes: 'device_id' }));
      dispatch(getEcosystemNetworkMetric({ ...payload, attributes: 'gateway_id' }));
      dispatch(getEcosystemThreatMetric({ ...payload, attributes: 'details', limit: 5 }));
      dispatch(getEcosystemThreatMetric({ ...payload, attributes: 'user', limit: 5 }));
      dispatch(getEcosystemThreatMetric({ ...payload, attributes: 'destination_fqdn', limit: 5 }));
      dispatch(getEcosystemThreatMetric({ ...payload, attributes: 'source_fqdn', limit: 5 }));
      dispatch(getEcosystemThreatMetric({ ...payload, attributes: 'threat_category', limit: 5 }));
      dispatch(getEcosystemThreatData(payload));
      dispatch(getEcosystemDetailCounters(payload));
      dispatch(getEcosystemThreatSeverity(payload));
    } catch (err) {
      // eslint-disable-next-line no-console
      console.log(err);
    } finally {
      setLoading(false);
    }
  }, [dispatch, selectedEcosystemUUID, timeStamp]);

  useEffect(() => {
    getData();
  }, [getData]);

  return (
    <>
      <Helmet>
        <title>Threat Report - {selectedEcosystem?.name ?? ''} - Sectrify</title>
      </Helmet>
      <PageTitleWrapper size="false">
        <Typography variant="h4" sx={{ mb: 4 }}>
          Summary Threat Report
        </Typography>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          rowGap={4}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', lg: 'center' }}
        >
          <Stack direction="row" alignItems="center" spacing={3}>
            <FormControl disabled={!ecosystems.length}>
              <InputLabel id="eco-select">Ecosystem</InputLabel>
              <Select
                labelId="eco-select"
                id="ecoSelect"
                value={selectedEcosystemUUID}
                label="Ecosystem"
                onChange={(e) => setSelectedEcosystemUUID(e.target.value)}
                sx={{ maxWidth: 480 }}
              >
                {ecosystemOptions.map((option) => (
                  <MenuItem key={option.label} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TimeWindows
              disabled={loading}
              options={timeWindowOptions}
              value={timeWindow}
              onChange={handleChangeTimeWindow}
            />
          </Stack>
          <RefreshTimer disabled={loading} value={refresh} onRefresh={handleRefresh} />
        </Stack>
      </PageTitleWrapper>
      <Container maxWidth="false" sx={{ height: 'fit-content', marginBottom: 4 }}>
        <Grid container columnSpacing={4} rowSpacing={8}>
          <Grid item xs={12}>
            <ThreatGeoLocation loading={loading} />
          </Grid>
          <Grid item md={12} xl={6}>
            <Utilization />
          </Grid>
          <Grid item md={12} xl={6}>
            <Security />
          </Grid>
          <Grid item xs={12}>
            <ThreatMetrics />
          </Grid>
        </Grid>
      </Container>
      {loading && <LoadingSpinner />}
    </>
  );
};

export default ThreatReport;
